import {
  HeaderMobileWrapper,
  HamburgerMenuWrapper,
  HamburgerMenuBody,
  HamburgerMenuClose,
  HamburgerMenuContent,
  MobileNavItem,
  MobileUserSection,
  MobileUserAvatar,
  MobileUserInfo,
  MobileUserMenu,
  MobileUserMenuItem,
  MobileTrialBanner,
  HeaderActions,
} from "./Styles";

import MikeLogo from "../../Assets/logo.png";
import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineClose, AiOutlineMenu, AiOutlineFileSearch, AiOutlineFileText } from "react-icons/ai";
import { TbMessages, TbPigMoney } from "react-icons/tb";
import { FaUsers } from "react-icons/fa";
import { FiGift, FiLogOut } from "react-icons/fi";
import { useOnClickOutside } from "usehooks-ts";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../Common/Hooks/useAuth";
import {
  ReportSectionNavMobile,
  ReportOutline,
  sectionsFromOutline,
} from "../../Pages/DueDiligence/LegalFiscalReport/components/ReportSectionNav";
import { useBusiness } from "../../Common/Hooks/useBusiness";
import { getAvatarColor } from "../../Common/Utils/getAvatarColor";
import { eventBus } from "../../Common/EventBus";
import { isAdmin } from "../../Common/Utils/isAdmin";
import { isSubscriptionActive } from "../../Common/Utils/subscriptionUtils";
import { UsersModal } from "../Sidebar/UsersModal";
import { ReferralModal } from "../Sidebar/ReferralModal";

import { MobileChatListDrawer } from "./MobileChatListDrawer";

export const HeaderMobile = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isChatListOpen, setIsChatListOpen] = useState(false);
  const location = useLocation();
  const isChatView = location.pathname === "/" || location.pathname.startsWith("/chat");

  return (
    <>
      <HeaderMobileWrapper>
        <img src={MikeLogo} alt={t('common.headerMobile.logoAlt')} style={{ height: '2.5rem', width: 'auto' }} />
        <HeaderActions>
          {isChatView && (
            <TbMessages
              onClick={() => setIsChatListOpen(true)}
              style={{ color: '#ffffff', cursor: 'pointer', width: '1.5rem', height: '1.5rem' }}
            />
          )}
          <AiOutlineMenu onClick={() => setIsOpen(true)} style={{ color: '#ffffff', cursor: 'pointer' }} />
        </HeaderActions>
      </HeaderMobileWrapper>
      <HamburguerMenu isOpen={isOpen} setIsOpen={setIsOpen} />
      {isChatView && (
        <MobileChatListDrawer isOpen={isChatListOpen} setIsOpen={setIsChatListOpen} />
      )}
    </>
  );
};

const HamburguerMenu = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, clientUser: client } = useAuth();
  const { business } = useBusiness();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string>("");
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean>(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userIsAdmin = isAdmin(business, client);

  const isCopilotActive = location.pathname === "/" || location.pathname.startsWith("/chat");
  const isDueDiligenceActive = location.pathname.startsWith("/due-diligence");
  const isContractsActive = location.pathname.startsWith("/contracts");
  const isReportPage = location.pathname.endsWith("/report");
  const [activeSectionId, setActiveSectionId] = useState("resumen");
  const [enabledSections, setEnabledSections] = useState<Record<string, boolean>>({});
  const [outline, setOutline] = useState<ReportOutline>([]);

  // Same source-of-truth as the desktop sidebar: the section list comes from
  // the outline event.
  const reportSections = sectionsFromOutline(outline);

  useEffect(() => {
    const handleChanged = (sectionId: string) => setActiveSectionId(sectionId);
    const handleEnabled = (sections: Record<string, boolean>) => setEnabledSections(sections);
    const handleOutline = (next: ReportOutline) => setOutline(next);
    eventBus.on("REPORT_SECTION_CHANGED", handleChanged);
    eventBus.on("REPORT_ENABLED_SECTIONS", handleEnabled);
    eventBus.on("REPORT_OUTLINE", handleOutline);
    return () => {
      eventBus.off("REPORT_SECTION_CHANGED", handleChanged);
      eventBus.off("REPORT_ENABLED_SECTIONS", handleEnabled);
      eventBus.off("REPORT_OUTLINE", handleOutline);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu]);

  useEffect(() => {
    const isMountedRef = { current: true };

    const loadSubscriptionTier = async () => {
      if (!business) {
        if (!isMountedRef.current) return;
        setSubscriptionTier("");
        setHasActiveSubscription(false);
        return;
      }

      // Staff-owned businesses always have access
      if (business.isStaffOwned) {
        if (!isMountedRef.current) return;
        setHasActiveSubscription(true);
        setSubscriptionTier(t('common.sidebar.subscriptionTier.staff'));
        return;
      }

      // Kept by GET /business, which reads it from our own table, not Stripe
      if (isSubscriptionActive(business.copilotSubscription)) {
        setHasActiveSubscription(true);
        setSubscriptionTier(t('common.sidebar.subscriptionTier.active'));
        return;
      }

      if (!isMountedRef.current) return;
      setHasActiveSubscription(false);
      if (business.trialEndDate) {
        const trialEndDate = new Date(business.trialEndDate);
        const now = new Date();
        if (trialEndDate > now) {
          if (!isMountedRef.current) return;
          setSubscriptionTier(t('common.sidebar.subscriptionTier.trial'));
          return;
        }
      }

      // If not on trial and no active subscription
      if (!isMountedRef.current) return;
      setSubscriptionTier("");
    };

    loadSubscriptionTier();

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id, business?.copilotSubscription?.status]);



  const trialBannerProps = useMemo(() => {
    if (hasActiveSubscription || location.pathname === '/choose-plan') {
      return null;
    }

    if (!business || !business.trialEndDate) {
      return null;
    }

    const trialEndDate = new Date(business.trialEndDate);
    const now = new Date();

    if (trialEndDate > now) {
      const hoursLeft = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60));
      let message = '';
      if (hoursLeft >= 24) {
        const daysLeft = Math.floor(hoursLeft / 24);
        message = t('common.headerMobile.trialBanner.expiresInDays', { count: daysLeft });
      } else {
        message = t('common.headerMobile.trialBanner.expiresInHours', { count: hoursLeft });
      }
      return {
        type: 'info' as const,
        message
      };
    } else {
      return {
        type: 'warning' as const,
        message: t('common.headerMobile.trialBanner.expired')
      };
    }
  }, [business, location.pathname, hasActiveSubscription, t]);

  const getUserInitials = () => {
    if (!client) return "U";
    const name = client.name || "";
    const parts = name.split(" ").filter(part => part.trim().length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (parts.length === 1 && parts[0].length > 0) {
      return parts[0][0].toUpperCase();
    }
    return "U";
  };

  const getUserName = () => {
    return client?.name || t('common.headerMobile.userMenu.fallbackName');
  };

  const handleClose = useCallback(() => {
    if (isOpen) setIsOpen(false);
  }, [isOpen, setIsOpen]);

  useOnClickOutside(ref, handleClose);

  const handleNavigate = (route: string) => {
    navigate(route);
    setIsOpen(false);
  };

  if (!business || !client) return null;

  return (
    <>
      <HamburgerMenuWrapper $isOpen={isOpen} ref={ref}>
        <HamburgerMenuBody>
          <HamburgerMenuClose>
            <span onClick={() => setIsOpen(false)}>
              <AiOutlineClose />
            </span>
          </HamburgerMenuClose>

          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', justifyContent: 'space-between', minHeight: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '1rem', flex: 1, overflowY: 'auto' }}>
              {/* Logo */}
              <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
                <img src={MikeLogo} alt={t('common.headerMobile.logoAlt')} style={{ width: '80%', maxWidth: '120px', height: 'auto' }} />
              </div>

              {/* Navigation Items */}
              <HamburgerMenuContent>
                <MobileNavItem
                  $isActive={isCopilotActive}
                  onClick={() => handleNavigate("/chat")}
                >
                  <TbMessages size={20} />
                  <span>{t('common.headerMobile.nav.copilot')}</span>
                </MobileNavItem>
                <MobileNavItem
                  $isActive={isDueDiligenceActive}
                  onClick={() => handleNavigate("/due-diligence")}
                >
                  <AiOutlineFileSearch size={20} />
                  <span>{t('common.headerMobile.nav.dueDiligence')}</span>
                </MobileNavItem>
                <MobileNavItem
                  $isActive={isContractsActive}
                  onClick={() => handleNavigate("/contracts")}
                >
                  <AiOutlineFileText size={20} />
                  <span>{t('common.headerMobile.nav.contracts')}</span>
                  <span style={{ fontSize: '0.55rem', fontWeight: 700, backgroundColor: '#DBEAFE', color: '#1E40AF', padding: '1px 4px', borderRadius: '3px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('common.headerMobile.nav.contractsBadge')}
                  </span>
                </MobileNavItem>
                {isReportPage && (
                  <ReportSectionNavMobile
                    activeSectionId={activeSectionId}
                    enabledSections={enabledSections}
                    sections={reportSections}
                    onSectionClick={(id) => {
                      eventBus.emit("REPORT_SCROLL_TO_SECTION", id);
                      setIsOpen(false);
                    }}
                    onToggleSection={(id) => {
                      eventBus.emit("REPORT_TOGGLE_SECTION", id);
                    }}
                  />
                )}
              </HamburgerMenuContent>

              {/* Trial banner */}
              {trialBannerProps && (
                <MobileTrialBanner $type={trialBannerProps.type} onClick={() => handleNavigate('/choose-plan')}>
                  <div style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                    {trialBannerProps.message}
                  </div>
                </MobileTrialBanner>
              )}
            </div>

            {/* User Section */}
            <MobileUserSection>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s',
                  width: '100%',
                  justifyContent: 'flex-start'
                }}
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <MobileUserAvatar
                  $bgColor={getAvatarColor(client?.email || "").bg}
                  $textColor={getAvatarColor(client?.email || "").text}
                >
                  {getUserInitials()}
                </MobileUserAvatar>
                <MobileUserInfo>
                  <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#ffffff' }}>
                    {getUserName()}
                  </div>
                  {subscriptionTier && (
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                      {subscriptionTier}
                    </div>
                  )}
                </MobileUserInfo>
              </div>

              {/* User Menu Dropdown */}
              {showUserMenu && (
                <MobileUserMenu ref={userMenuRef}>
                  <div style={{ padding: '0.75rem', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', marginBottom: '0.25rem' }}>
                      {client?.name || t('common.headerMobile.userMenu.fallbackName')}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {client?.email || ""}
                    </div>
                  </div>
                  {userIsAdmin && (
                    <MobileUserMenuItem onClick={() => {
                      setShowUsersModal(true);
                      setShowUserMenu(false);
                      setIsOpen(false);
                    }}>
                      <FaUsers size={16} />
                      <span>{t('common.headerMobile.userMenu.team')}</span>
                    </MobileUserMenuItem>
                  )}
                  {userIsAdmin && (
                    <MobileUserMenuItem onClick={() => {
                      eventBus.emit("OPEN_SUBSCRIPTION_MODAL");
                      setShowUserMenu(false);
                      setIsOpen(false);
                    }}>
                      <TbPigMoney size={16} />
                      <span>{t('common.headerMobile.userMenu.subscription')}</span>
                    </MobileUserMenuItem>
                  )}
                  <MobileUserMenuItem onClick={() => {
                    setShowReferralModal(true);
                    setShowUserMenu(false);
                    setIsOpen(false);
                  }}>
                    <FiGift size={16} />
                    <span>{t('common.headerMobile.userMenu.referral')}</span>
                  </MobileUserMenuItem>
                  <MobileUserMenuItem onClick={() => {
                    logout();
                    setIsOpen(false);
                  }} style={{ color: '#ef4444' }}>
                    <FiLogOut size={16} />
                    <span>{t('common.headerMobile.userMenu.logout')}</span>
                  </MobileUserMenuItem>
                </MobileUserMenu>
              )}
            </MobileUserSection>
          </div>
        </HamburgerMenuBody>
      </HamburgerMenuWrapper>

      {/* Modals for Users and Subscription */}
      {showUsersModal && (
        <UsersModal
          onClose={() => setShowUsersModal(false)}
          onOpenSubscription={() => {
            setShowUsersModal(false);
            eventBus.emit("OPEN_SUBSCRIPTION_MODAL");
          }}
        />
      )}

      {showReferralModal && <ReferralModal onClose={() => setShowReferralModal(false)} />}

    </>
  );
};
